import { useState } from 'react'
import { useI18n } from '../i18n'
import {
  useMyYards, useHorses, useMyInvites, useAcceptInvite,
  useBoardHorse, useUnboardHorse, useSetFarmHidden, useMembers,
  useMyTickets, useCreateTicket, useMyInvoices,
} from '../api/hooks'
import {
  FARM_SHARE_CATEGORIES, TICKET_CATEGORIES, TICKET_PRIORITIES, LIVERY_PACKAGES,
  formatPence,
  type FarmShareCategory, type MyYard, type Horse, type LiveryPackage,
} from '@horsehub/shared'
import { fmtDate } from '../lib/format'
import { PageSpinner, EmptyState, Modal, Input, Textarea, Select, Spinner } from '../components/ui'
import { TicketCard } from './FarmTickets'

const CAT_LABEL: Record<FarmShareCategory, string> = {
  medical: 'farm.my.catMedical',
  documents: 'farm.my.catDocuments',
  reminders: 'farm.my.catReminders',
  info: 'farm.my.catInfo',
}

export function MyFarmPage() {
  const { t } = useI18n()
  const { data: yards, isLoading } = useMyYards()
  const { data: horses } = useHorses()
  const { data: invites } = useMyInvites()
  const accept = useAcceptInvite()

  if (isLoading) return <PageSpinner />
  const pending = invites ?? []
  const joined = yards ?? []

  return (
    <main className="mx-auto max-w-3xl px-5 py-8">
      <h1 className="text-2xl font-extrabold tracking-tight">{t('farm.my.title')}</h1>
      <p className="mt-1 text-brand-600 dark:text-brand-300">{t('farm.my.subtitle')}</p>

      {/* Pending invitations — accepting joins the yard (doesn't move other horses) */}
      {pending.length > 0 && (
        <div className="mt-6 space-y-3">
          {pending.map((inv) => (
            <div key={inv.token} className="card flex flex-wrap items-center justify-between gap-3 border-2 border-brand-300/70 dark:border-brand-600/60 p-5">
              <div>
                <div className="font-bold">📨 {inv.farmName ? t('farm.accept.invitedYou', { farm: inv.farmName }) : t('farm.accept.heading')}</div>
                <div className="text-sm text-brand-600 dark:text-brand-300">{t('farm.accept.body')}</div>
              </div>
              <button onClick={() => accept.mutate(inv.token)} disabled={accept.isPending} className="btn-primary disabled:opacity-60">
                {accept.isPending ? t('farm.accept.busy') : t('farm.accept.cta')}
              </button>
            </div>
          ))}
        </div>
      )}

      {joined.length === 0 ? (
        pending.length === 0 && (
          <div className="mt-6"><EmptyState icon="🏡" title={t('farm.my.none')} body={t('farm.my.noneBody')} /></div>
        )
      ) : (
        <>
          <h2 className="mb-1 mt-8 text-lg font-bold">{t('farm.my.joinedHeading')}</h2>
          <p className="mb-3 text-sm text-brand-500 dark:text-brand-400">{t('farm.my.joinedHint')}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {joined.map((y) => (
              <div key={y.id} className="card flex items-center gap-3 p-4">
                <div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-2xl bg-brand-100 dark:bg-brand-800 text-xl">
                  {y.avatarUrl ? <img src={y.avatarUrl} alt="" className="h-full w-full object-cover" /> : '🏡'}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-bold">{y.name}</span>
                    {y.owned && <span className="chip shrink-0 !bg-brand-200/70 dark:!bg-brand-700 !text-brand-700 dark:!text-brand-100">{t('farm.my.yourYard')}</span>}
                  </div>
                  <div className="truncate text-sm text-brand-600 dark:text-brand-300">
                    {[y.addressLine, y.city, y.postcode, y.country].filter(Boolean).join(', ')}
                  </div>
                  {y.emergencyPhone && (
                    <div className="truncate text-xs text-brand-500 dark:text-brand-400">
                      🚨 {t('farm.uk.emergency')}: {[y.emergencyName, y.emergencyPhone].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Who else is on the yard — fellow liveries + staff (no emails for liveries) */}
          {joined.map((y) => <YardPeople key={y.id} yard={y} />)}

          {/* Each horse → which yard it lives at (can differ per horse) */}
          <h2 className="mb-1 mt-10 text-lg font-bold">{t('farm.my.horsesHeading')}</h2>
          <p className="mb-3 text-sm text-brand-500 dark:text-brand-400">{t('farm.my.placeHint')}</p>
          <div className="space-y-4">
            {(horses ?? []).map((h) => <HorseStableRow key={h.id} horse={h} yards={joined} />)}
          </div>

          <MyInvoices />

          <BoarderTickets yards={joined} horses={horses ?? []} />
        </>
      )}
    </main>
  )
}

/** Invoices the yard has sent me — with plain-English payment guidance. */
function MyInvoices() {
  const { t, locale } = useI18n()
  const { data: invoices, isLoading } = useMyInvoices()

  return (
    <div className="mt-10">
      <h2 className="mb-3 text-lg font-bold">{t('farm.invoices.myTitle')}</h2>
      {isLoading ? (
        <div className="grid place-items-center py-6"><Spinner /></div>
      ) : (invoices ?? []).length === 0 ? (
        <p className="text-sm text-brand-600 dark:text-brand-300">{t('farm.invoices.emptyMine')}</p>
      ) : (
        <div className="space-y-3">
          {(invoices ?? []).map((inv) => (
            <div key={inv.id} className="card p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{inv.farmName}</span>
                    <span
                      className={`chip !px-2 !py-0.5 text-xs ${
                        inv.status === 'paid'
                          ? '!bg-emerald-100 !text-emerald-700 dark:!bg-emerald-900/40 dark:!text-emerald-300'
                          : inv.status === 'sent'
                            ? '!bg-amber-100 !text-amber-700 dark:!bg-amber-900/40 dark:!text-amber-300'
                            : ''
                      }`}
                    >
                      {t(`farm.invoices.st.${inv.status}`)}
                    </span>
                  </div>
                  <div className="truncate text-sm text-brand-600 dark:text-brand-300">
                    {inv.number}
                    {inv.periodLabel ? ` · ${inv.periodLabel}` : ''}
                    {inv.dueDate ? ` · ${t('farm.invoices.due', { date: fmtDate(inv.dueDate, locale) })}` : ''}
                  </div>
                </div>
                <div className="text-lg font-extrabold">{formatPence(inv.totalPence)}</div>
              </div>
              <ul className="mt-2 text-sm text-brand-600 dark:text-brand-300">
                {inv.lines.map((l, i) => (
                  <li key={i} className="flex justify-between gap-3 py-0.5">
                    <span className="min-w-0 truncate">{l.description}</span>
                    <span className="shrink-0">{formatPence(l.amountPence)}</span>
                  </li>
                ))}
              </ul>
              {inv.status === 'sent' && (
                <p className="mt-2 text-sm text-brand-500 dark:text-brand-400">{t('farm.invoices.payNote')}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function HorseStableRow({ horse, yards }: { horse: Horse; yards: MyYard[] }) {
  const { t } = useI18n()
  const board = useBoardHorse()
  const unboard = useUnboardHorse()
  const setHidden = useSetFarmHidden(horse.id)
  const hidden = horse.farmHidden ?? []
  const busy = board.isPending || unboard.isPending
  const [loc, setLoc] = useState(horse.stableLocation ?? '')
  const saveLoc = () => {
    const next = loc.trim()
    if (!horse.farmId || next === (horse.stableLocation ?? '')) return
    board.mutate({ horseId: horse.id, farmId: horse.farmId, stableLocation: next })
  }

  const onYardChange = (yardId: string) => {
    if (yardId) board.mutate({ horseId: horse.id, farmId: yardId })
    else unboard.mutate(horse.id)
  }
  const toggle = (cat: FarmShareCategory) => {
    const next = hidden.includes(cat) ? hidden.filter((c) => c !== cat) : [...hidden, cat]
    setHidden.mutate(next)
  }
  const options = [
    { value: '', label: t('farm.my.notStabled') },
    ...yards.map((y) => ({ value: y.id, label: y.owned ? `${y.name} (${t('farm.my.yourYard')})` : y.name })),
  ]

  return (
    <div className="card p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="font-bold">{horse.name}</div>
        <div className="flex flex-wrap gap-3">
          <div className="min-w-[12rem]">
            <Select label={t('farm.my.stabledAt')} value={horse.farmId ?? ''} onChange={onYardChange} options={options} />
          </div>
          {horse.farmId && (
            <div className="min-w-[10rem]">
              <Select
                label={t('farm.packages.label')}
                value={horse.liveryPackage ?? ''}
                onChange={(v) =>
                  v && board.mutate({ horseId: horse.id, farmId: horse.farmId!, liveryPackage: v as LiveryPackage })
                }
                options={[
                  { value: '', label: t('farm.packages.none') },
                  ...LIVERY_PACKAGES.map((p) => ({ value: p, label: t(`farm.packages.${p}`) })),
                ]}
              />
            </div>
          )}
        </div>
      </div>

      {horse.farmId && (
        <div className="mt-4 max-w-xs">
          <Input
            label={t('farm.my.stableLocation')}
            value={loc}
            onChange={setLoc}
            onBlur={saveLoc}
            placeholder={t('farm.my.stableLocationPlaceholder')}
          />
        </div>
      )}

      {horse.farmId && (
        <div className="mt-4">
          <div className="mb-2 text-xs font-medium text-brand-500 dark:text-brand-400">{t('farm.my.visibleToYard')}</div>
          <div className="flex flex-wrap gap-2">
            {FARM_SHARE_CATEGORIES.map((cat) => {
              const shared = !hidden.includes(cat)
              return (
                <button
                  key={cat}
                  onClick={() => toggle(cat)}
                  disabled={setHidden.isPending || busy}
                  aria-pressed={shared}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition disabled:opacity-60 ${
                    shared
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                      : 'bg-brand-100 text-brand-500 line-through dark:bg-brand-800'
                  }`}
                >
                  {shared ? '✓ ' : ''}{t(CAT_LABEL[cat])}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

/** "Who's on the yard" — fellow liveries + staff for one yard the caller belongs to. */
function YardPeople({ yard }: { yard: MyYard }) {
  const { t } = useI18n()
  const { data: members, isLoading } = useMembers(yard.id)
  const people = members ?? []
  if (isLoading || people.length === 0) return null

  return (
    <div className="mt-8">
      <h2 className="text-lg font-bold">{t('farm.my.peopleHeading')} · {yard.name}</h2>
      <p className="mb-3 text-sm text-brand-500 dark:text-brand-400">{t('farm.my.peopleHint')}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {people.map((m) => (
          <div key={m.userId} className="card flex items-center gap-3 p-3">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand-100 dark:bg-brand-800 text-sm font-bold">
              {m.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">{m.name}</div>
              <div className="text-xs text-brand-500 dark:text-brand-400">
                {t(`farm.roles.${m.role}`)}
                {m.horseCount ? ` · ${t('farm.members.horsesHere', { n: m.horseCount })}` : ''}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function BoarderTickets({ yards, horses }: { yards: MyYard[]; horses: Horse[] }) {
  const { t, locale } = useI18n()
  const { data: tickets, isLoading } = useMyTickets()
  const [open, setOpen] = useState(false)

  return (
    <div className="mt-10">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-bold">{t('tickets.title')}</h2>
        <button onClick={() => setOpen(true)} className="btn-primary !px-4 !py-2 text-sm">{t('tickets.raise')}</button>
      </div>
      {isLoading ? (
        <div className="grid place-items-center py-6"><Spinner /></div>
      ) : (tickets ?? []).length === 0 ? (
        <p className="text-sm text-brand-600 dark:text-brand-300">{t('tickets.noneMine')}</p>
      ) : (
        <div className="space-y-4">{(tickets ?? []).map((tk) => <TicketCard key={tk.id} ticket={tk} locale={locale} />)}</div>
      )}
      {open && <RaiseTicketModal yards={yards} horses={horses} onClose={() => setOpen(false)} />}
    </div>
  )
}

function RaiseTicketModal({ yards, horses, onClose }: { yards: MyYard[]; horses: Horse[]; onClose: () => void }) {
  const { t } = useI18n()
  const create = useCreateTicket()
  const [farmId, setFarmId] = useState(yards[0]?.id ?? '')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [category, setCategory] = useState('general')
  const [priority, setPriority] = useState('normal')
  const [horseId, setHorseId] = useState('')

  const submit = async () => {
    if (!subject.trim() || !farmId) return
    await create.mutateAsync({ farmId, subject, message, category, priority, horseId: horseId || undefined })
    onClose()
  }
  const horseOptions = [{ value: '', label: t('tickets.horseNone') }, ...horses.map((h) => ({ value: h.id, label: h.name }))]

  return (
    <Modal title={t('tickets.raise')} onClose={onClose}>
      <div className="space-y-4">
        <Select label={t('tickets.yard')} value={farmId} onChange={setFarmId} options={yards.map((y) => ({ value: y.id, label: y.name }))} />
        <Input label={t('tickets.subject')} value={subject} onChange={setSubject} placeholder={t('tickets.subjectPlaceholder')} required />
        <Textarea label={t('tickets.message')} value={message} onChange={setMessage} placeholder={t('tickets.messagePlaceholder')} rows={4} />
        <div className="grid grid-cols-2 gap-4">
          <Select label={t('tickets.category')} value={category} onChange={setCategory} options={TICKET_CATEGORIES.map((c) => ({ value: c, label: t(`tickets.cat.${c}`) }))} />
          <Select label={t('tickets.priority')} value={priority} onChange={setPriority} options={TICKET_PRIORITIES.map((p) => ({ value: p, label: t(`tickets.prio.${p}`) }))} />
        </div>
        <Select label={t('tickets.horse')} value={horseId} onChange={setHorseId} options={horseOptions} />
        <button disabled={create.isPending || !subject.trim() || !farmId} onClick={submit} className="btn-primary w-full disabled:opacity-60">
          {create.isPending ? t('tickets.sending') : t('tickets.submit')}
        </button>
      </div>
    </Modal>
  )
}
